/**
 * @file HowToBecomeAnRbt.jsx
 * @description Standalone educational/career-intent SEO page at
 *   /how-to-become-an-rbt — explains, at a category level only, the general
 *   BACB pathway to becoming a Registered Behavior Technician (RBT), then
 *   bridges into how ABAcases helps once someone is certified (or in
 *   process) and ready to find real case work. Deliberately conservative:
 *   no specific numeric requirements (hours, question counts, fees,
 *   PDU counts; only the verified 2-year renewal cycle and the "at least 18"
 *   eligibility line, each pointed back to bacb.com/rbt), no
 *   certification/training/supervision claims by ABAcases,
 *   no active case or provider counts. Links out once, for real, to the
 *   official BACB RBT page as the authoritative source. Mirrors the
 *   structure of AboutAbaCases.jsx / CityRolePage.jsx (hero, prose + card
 *   sections, FAQ with JSON-LD, gradient closing CTA, PublicFooter).
 *
 *   Rendered under TopNav (guest nav), so it has no brand header of its own.
 * @module components/HowToBecomeAnRbt
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, DollarIcon, CalendarIcon, BuildingIcon, BellIcon,
  InfoIcon, ExternalLinkIcon, AlertTriangleIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import GuideReviewStamp from "./GuideReviewStamp";
import { MoreDetails } from "./Disclosure";
import { GuideAtAGlance, GuideYourPath, GuideYourPathChips } from "./GuideShared";
import { setSignupIntent } from "../lib/signupIntent";
import { useFaqJsonLd, useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

const BACB_RBT_URL = "https://www.bacb.com/rbt/";
const BACB_EXAM_URL = "https://www.bacb.com/examination-information/";

// General-pathway steps, described in categories only — no specific hour
// counts, question counts, fees, or ages. Exact current requirements live at
// bacb.com, not here.
const STEPS = [
  { titleKey: "howToBecomeRbt.step1Title", bodyKey: "howToBecomeRbt.step1Body" },
  { titleKey: "howToBecomeRbt.step2Title", bodyKey: "howToBecomeRbt.step2Body" },
  { titleKey: "howToBecomeRbt.step3Title", bodyKey: "howToBecomeRbt.step3Body" },
  { titleKey: "howToBecomeRbt.step4Title", bodyKey: "howToBecomeRbt.step4Body" },
  { titleKey: "howToBecomeRbt.step5Title", bodyKey: "howToBecomeRbt.step5Body" },
  { titleKey: "howToBecomeRbt.step6Title", bodyKey: "howToBecomeRbt.step6Body" },
  { titleKey: "howToBecomeRbt.step7Title", bodyKey: "howToBecomeRbt.step7Body" },
];

const PREP_ITEMS = [
  { Icon: CalendarIcon, titleKey: "howToBecomeRbt.prep1Title", bodyKey: "howToBecomeRbt.prep1Body" },
  { Icon: BuildingIcon, titleKey: "howToBecomeRbt.prep2Title", bodyKey: "howToBecomeRbt.prep2Body" },
  { Icon: MapPinIcon, titleKey: "howToBecomeRbt.prep3Title", bodyKey: "howToBecomeRbt.prep3Body" },
  { Icon: DollarIcon, titleKey: "howToBecomeRbt.prep4Title", bodyKey: "howToBecomeRbt.prep4Body" },
];

// SINGLE SOURCE OF TRUTH for FAQ content: these i18n keys drive BOTH the visible
// FAQ cards AND the FAQPage JSON-LD (derived in-component from the same keys via
// faqJsonLd — see below), so schema and visible text can never diverge. faq2
// keeps its BACB link split into before/after fragments so <BacbLink> stays a
// real inline anchor; the JSON-LD reconstructs the same text as a plain string.
const FAQ_ITEMS = [
  { qKey: "howToBecomeRbt.faq1Q", aKey: "howToBecomeRbt.faq1A" },
  { qKey: "howToBecomeRbt.faq2Q", beforeLinkKey: "howToBecomeRbt.faq2ABeforeLink", afterLinkKey: "howToBecomeRbt.faq2AAfterLink", externalLink: true },
  { qKey: "howToBecomeRbt.faq3Q", aKey: "howToBecomeRbt.faq3A" },
  { qKey: "howToBecomeRbt.faq4Q", aKey: "howToBecomeRbt.faq4A" },
];

export default function HowToBecomeAnRbt({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "How to Become an RBT | Steps for Starting ABA Work",
    description: "Learn the basic steps to become an RBT, what to confirm with the BACB, and how ABAcases helps new providers find case alerts and track hours and payments.",
    canonical: "/how-to-become-an-rbt",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-how-to-become-an-rbt", [
    { name: "Home", url: "/" },
    { name: "How to Become an RBT", url: "/how-to-become-an-rbt" },
  ]);
  const faqJsonLd = FAQ_ITEMS.map((f) => ({
    q: t(f.qKey),
    a: f.externalLink ? `${t(f.beforeLinkKey)} bacb.com/rbt ${t(f.afterLinkKey)}` : t(f.aKey),
  }));
  useFaqJsonLd("faq-jsonld-how-to-become-an-rbt", faqJsonLd);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  // Real, external, off-domain link to the authoritative BACB source — the one
  // place on this page that intentionally leaves abacases.com.
  const BacbLink = ({ href = BACB_RBT_URL, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {children} <ExternalLinkIcon size={12} color={INDIGO} />
    </a>
  );

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: "/how-to-become-an-rbt/" },
      ga4Params: ctaMeta({
        pagePath: "/how-to-become-an-rbt/",
        pageType: "resource",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "resource",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t('howToBecomeRbt.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('howToBecomeRbt.heroTitle') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('howToBecomeRbt.badgeCareerGuide')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('howToBecomeRbt.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('howToBecomeRbt.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t('howToBecomeRbt.heroBody')}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" /> {t('howToBecomeRbt.ctaJoinAlerts')}
            </button>
          </div>
        </div>
      </header>
      <GuideYourPathChips current="how-to-become-an-rbt" onNavigate={onNavigate} isMobile={isMobile} />

      {/* ── At a glance ──────────────────────────────────────── */}
      <GuideAtAGlance
        isMobile={isMobile}
        title={t('guideShared.atGlanceTitle')}
        items={[1, 2, 3, 4, 5, 6, 7].map((n) => t(`howToBecomeRbt.atGlanceItem${n}`))}
        confirmPre={t('howToBecomeRbt.atGlanceConfirm')}
        confirmHref={BACB_RBT_URL}
        confirmLabel="bacb.com/rbt"
      />

      {/* ── Disclaimer banner ────────────────────────────────── */}
      <div style={{ background: "#eef2ff", borderBottom: `1px solid ${BORDER}`, padding: isMobile ? "16px 18px" : "18px 48px" }}>
        <div style={{ maxWidth: maxW, margin: "0 auto", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <InfoIcon size={17} color={INDIGO} style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 13.5, color: NAVY, fontWeight: 600, lineHeight: 1.6 }}>
            {t('howToBecomeRbt.disclaimerText')} <BacbLink>bacb.com/rbt</BacbLink>.
          </span>
        </div>
      </div>

      {/* ── 1. What is an RBT? ────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeRbt.whatIsTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeRbt.whatIsPara1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeRbt.whatIsPara2')}
          </p>
        </div>
      </section>

      {/* ── 2. Basic steps to become an RBT ──────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeRbt.stepsTitle')}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, marginBottom: 22 }}>
            {t('howToBecomeRbt.stepsIntro')}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
            {STEPS.map((s, i) => (
              <div key={s.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: INDIGO, color: "#fff", fontWeight: 800, fontSize: 13.5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t(s.titleKey)}</div>
                  <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.6 }}>{t(s.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.7 }}>
            {t('howToBecomeRbt.stepsFooterText')} <BacbLink>bacb.com/rbt</BacbLink>.
          </p>
        </div>
      </section>

      {/* ── 3. Not certified yet? Avoid training scams ───────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <div data-testid="scam-callout" style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 14, padding: isMobile ? "20px" : "24px 26px" }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AlertTriangleIcon size={19} color="#b45309" />
            </div>
            <div>
              <h2 style={{ fontSize: isMobile ? 19 : 22, fontWeight: 900, color: NAVY, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
                {t('howToBecomeRbt.scamTitle')}
              </h2>
              <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.75, margin: "0 0 10px" }}>
                {t('howToBecomeRbt.scamBody')}
              </p>
              <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, margin: 0 }}>
                {t('howToBecomeRbt.scamLinkPre')}{" "}
                <SeoLink to="rbt-interview-questions" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.scamLinkText')}</SeoLink>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. What changed for RBTs on January 1, 2026 ──────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeRbt.whyMattersTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeRbt.whyMattersIntro')}
          </p>
          <ul data-testid="rbt-2026-changes" style={{ margin: "0 0 16px", paddingLeft: 22, fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {[1, 2, 3, 4].map((n) => (
              <li key={n} style={{ marginBottom: 6 }}>{t(`howToBecomeRbt.whyMattersItem${n}`)}</li>
            ))}
          </ul>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeRbt.whyMattersConfirmPre')}{" "}
            <BacbLink>bacb.com/rbt</BacbLink>{t('howToBecomeRbt.whyMattersConfirmMid')}{" "}
            <BacbLink href={BACB_EXAM_URL}>{t('howToBecomeRbt.newExamLinkText')}</BacbLink>.
          </p>
        </div>
      </section>

      {/* ── 5. What to prepare before applying for ABA cases ── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeRbt.prepTitle')}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 20 }}>
            {PREP_ITEMS.map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{t(c.titleKey)}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(c.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeRbt.prepFooterPart1')}{" "}
            <SeoLink to="rbt-resume-guide" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.resumeGuideLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.prepFooterPart2')}{" "}
            <SeoLink to="rbt-vs-bt-vs-bcba-vs-bcaba" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.roleComparisonLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.prepFooterPart3')}
          </p>
        </div>
      </section>

      {/* ── 6. How ABAcases helps after you are ready to work ── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeRbt.helpsTitle')}
          </h2>
          <p data-testid="rbt-helps-short" style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, margin: "0 0 10px" }}>
            {t('howToBecomeRbt.helpsShortPre')}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.caseAlertsLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.helpsShortPost')} {t('howToBecomeRbt.helpsShort2')}
          </p>
          <MoreDetails text="" extra={(
            <>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, margin: "8px 0 0" }}>
            {t('howToBecomeRbt.helpsPara1Part1')}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.caseAlertsLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.helpsPara1Part2')}{" "}
            <SeoLink to="rbt-jobs-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.miamiLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.helpsPara1Part3')}{" "}
            <SeoLink to="rbt-jobs-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.charlotteLinkText')}</SeoLink>
            {t('howToBecomeRbt.helpsPara1Part4')}{" "}
            <SeoLink to="rbt-timesheet" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.timesheetLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.helpsPara1Part5')}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.trackerLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.helpsPara1Part6')}
          </p>
            </>
          )} />
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginTop: 14 }}>
            {t('howToBecomeRbt.helpsPara2Part1')}{" "}
            <SeoLink to="about-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeRbt.aboutLinkText')}</SeoLink>{" "}
            {t('howToBecomeRbt.helpsPara2Part2')}
          </p>
        </div>
      </section>

      {/* ── 7. FAQ ────────────────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>
            {t('howToBecomeRbt.faqTitle')}
          </h2>
          {FAQ_ITEMS.map((f, i) => (
            <div key={i} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px", marginBottom: 10 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: NAVY, marginBottom: 8 }}>{t(f.qKey)}</div>
              <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.7 }}>
                {f.externalLink ? (
                  <>
                    {t(f.beforeLinkKey)} <BacbLink>bacb.com/rbt</BacbLink> {t(f.afterLinkKey)}
                  </>
                ) : t(f.aKey)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Source/verification note ──────────────────────────── */}
      <div style={{ padding: isMobile ? "20px 18px" : "22px 48px", textAlign: "center", background: "#fff" }}>
        <p style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6, maxWidth: 640, margin: "0 auto" }}>
          {t('howToBecomeRbt.sourceNote')}
        </p>
      </div>

      <GuideYourPath current="how-to-become-an-rbt" onNavigate={onNavigate} isMobile={isMobile} />
      <GuideReviewStamp slug="how-to-become-an-rbt" />
      <PublicFooter />
    </div>
  );
}
